import {
  NewOrderImpressionsMode,
  NewOrderScenario,
  NewOrderType,
  TNewOrderCountry,
  TNewOrderForm,
  TNewOrderImpressionsMode,
  TViewsKeyword,
} from "@/entities/new-order";
import {
  getDatesListFromCountry,
  getKeysFromCountry,
} from "@/entities/new-order/utils";
import { calcImpressions } from "@/features/new-order/country-item/lib";
import { useAppSelector } from "@/Hooks/redux/useAppSelector";
import { getAppStore } from "@/shared/helpers";
import { Checkbox } from "@/shared/ui/checkbox";
import Hint from "@/shared/ui/hint";
import BasicButton from "@/UI/BasicButton";
import { Badge, Flex, SegmentedControl, Stack, Text } from "@mantine/core";
import { ChangeEvent, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { CRNumberInput } from "./inputs";

type TCountryImpressionsBlockProps = {
  countryIndex: number;
};

export const CountryImpressionsBlock = ({
  countryIndex,
}: TCountryImpressionsBlockProps) => {
  const { user } = useAppSelector((state) => state.clientDataSlice);
  const { control, watch, setValue } = useFormContext<TNewOrderForm>();

  const [globalCr, setGlobalCr] = useState<number>(100);

  const { update } = useFieldArray({
    control,
    name: "countriesList",
  });

  const { type, app, countriesList, activeScenario } = watch();

  const country = countriesList[countryIndex];

  const toggleImpressions = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked) {
      setValue("type", NewOrderType.install);
    } else {
      setValue("type", NewOrderType.install_with_views);
    }
  };

  const { impressions } = country;

  const handleSetCRValue = (value: number) => {
    setGlobalCr(value);

    const dates = getDatesListFromCountry(country);
    const keys = getKeysFromCountry(country);

    const newCountry: TNewOrderCountry = JSON.parse(JSON.stringify(country));

    dates.forEach((date) => {
      keys.forEach((key) => {
        const dateKeywords = newCountry.keywords[date];
        if (Array.isArray(dateKeywords)) {
          return;
        }
        if (
          typeof (dateKeywords as Record<string, unknown>)[key] === "object"
        ) {
          const keywords: TViewsKeyword = newCountry.keywords as TViewsKeyword;
          keywords[date][key].views = calcImpressions(
            keywords[date][key].installs,
            value,
          );
          keywords[date][key].CR = value;
        }
      });
    });

    update(countryIndex, newCountry);
  };

  const handleSetImpressionsMode = (type: string) => {
    const newCountry: TNewOrderCountry = JSON.parse(JSON.stringify(country));
    const dates = getDatesListFromCountry(newCountry);
    const keys = getKeysFromCountry(newCountry);

    // Сбрасываем глобальный CR
    setGlobalCr(100);

    // При переключении режима очищаем соответствующие поля
    dates.forEach((date) => {
      keys.forEach((key) => {
        const dateKeywords = newCountry.keywords[date];
        if (Array.isArray(dateKeywords)) {
          return;
        }
        if (
          typeof (dateKeywords as Record<string, unknown>)[key] === "object"
        ) {
          const keywords: TViewsKeyword = newCountry.keywords as TViewsKeyword;

          if (type === NewOrderImpressionsMode.CR) {
            // При переключении на CR: очищаем views (они будут рассчитываться)
            keywords[date][key].views = 0;
          } else if (type === NewOrderImpressionsMode.Impressions) {
            // При переключении на Impressions: очищаем CR и views
            keywords[date][key].CR = 100;
            keywords[date][key].views = 0;
          }
        }
      });
    });

    newCountry.impressions = { type: type as TNewOrderImpressionsMode };

    update(countryIndex, newCountry);
  };

  return (
    <Stack gap={16}>
      <Flex align="center" gap="sm" p="sm" pos="relative">
        <Text fw="bold">Impressions </Text>
        <Badge size="xs" top={-7} pos="relative">
          NEW
        </Badge>
        <Hint
          popupText={`Global CR — even distribution of impressions across all keywords and campaign days.
          The cost of one impression equals the installation cost = ${user?.a_view_cost}$
          You can set individual values for each keyword.
          Set the target CR: the lower the percentage, the more impressions will be added.
          Set the number of impressions: specify a custom total number of impressions for each keyword per campaign day.
        `}
        />
        <Checkbox
          name={"impressions-" + countryIndex}
          checked={type === NewOrderType.install_with_views}
          onChange={toggleImpressions}
          isDisabled={
            !app ||
            getAppStore(app.url) === "google" ||
            activeScenario === NewOrderScenario.extend ||
            activeScenario === NewOrderScenario.duplicate
          }
        />
      </Flex>
      {impressions?.type && type === NewOrderType.install_with_views && (
        <Stack gap={16}>
          <SegmentedControl
            size="md"
            w={345}
            color="brand-blue"
            bg="white"
            fw={400}
            value={impressions?.type}
            onChange={handleSetImpressionsMode}
            data={[
              {
                label: "Conversion rate (CR)",
                value: NewOrderImpressionsMode.CR,
              },
              {
                label: "Impressions",
                value: NewOrderImpressionsMode.Impressions,
              },
            ]}
          />
          {impressions?.type === NewOrderImpressionsMode.CR && (
            <Flex gap={32} align="center">
              <Text>Conversion rate (Global CR)</Text>
              <CRNumberInput
                hideControls
                value={globalCr}
                onChange={handleSetCRValue}
                size="md"
                w={120}
                suffix="%"
                onFocus={(e) => e.target.select()}
              />
              <BasicButton
                onClick={() => {
                  handleSetCRValue(100);
                }}
                size="sm"
                text="Reset CR"
                variant="red"
              />
            </Flex>
          )}
        </Stack>
      )}
    </Stack>
  );
};
